/**
 * @fileoverview Acciones para el envío de mensajes y archivos en el inbox, soportando múltiples plataformas.
 * Proporciona funciones para despachar mensajes, archivos y respuestas rápidas usando SendMessageModelMC.
 * @module actions/inbox/sendMessageActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 3.1.0
 */

/**
 * @typedef {Object} MessageBody
 * @description Estructura de datos para el cuerpo de un mensaje.
 * @property {string|number} customer_id - ID del cliente destinatario.
 * @property {string} text - Texto del mensaje.
 * @property {string} [platform] - Plataforma destino (whatsapp, messenger, instagram, etc.).
 * @property {Array<Object>} [files] - Archivos adjuntos al mensaje.
 * @property {Object} [metadata] - Metadatos adicionales.
 */

import SendMessageModelMC from '../../presentation/models/inbox/SendMessageModelMC';
import FastAnswersModelMC from '../../presentation/models/inbox/FastAnswersModelMC';

/**
 * Acción asíncrona para despachar un mensaje según la plataforma seleccionada.
 *
 * @function dispatchMessageAction
 * @category Actions Layer
 * @param {Object} params - Parámetros del mensaje, incluyendo plataforma y cuerpo.
 * @param {string} params.platform - Plataforma destino.
 * @param {...Object} body - Resto de propiedades del mensaje.
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @throws {Error} Si ocurre un error en el envío del mensaje.
 * @example
 * await dispatchMessageAction({ platform: 'whatsapp', text: 'Hola' })(dispatch);
 * @see {@link SendMessageModelMC}
 */
export const dispatchMessageAction = (body) => async (dispatch) => {
  try {
    await SendMessageModelMC.sendMessage(body);
  } catch (error) {
    dispatch({
      type: 'LOAD_DATA_FAILURE',
      error: error.message || "An unexpected error occurred."
    });
  }
};

/**
 * Acción asíncrona para despachar respuestas rápidas a un cliente por WhatsApp u otras plataformas soportadas.
 *
 * Implementa el patrón de **Actions Layer** con separación estricta de responsabilidades,
 * enfocándose únicamente en la operación de envío de respuestas rápidas mediante el modelo FastAnswersModelMC.
 *
 * @function dispatchFastAnswersAction
 * @category Actions Layer
 * @module actions/inbox/sendMessageActions
 * @since 2.0.0
 * @version 3.1.1 - Documentación JSDoc optimizada y alineada a estándares Mercately
 *
 * @description
 * **Arquitectura Optimizada (v3.1.1):**
 * - ✅ Solo opera en capa de acciones (Actions Layer)
 * - ✅ Sin lógica de UI ni validación de formularios
 * - ✅ Manejo robusto de errores y despacho de fallos
 * - ✅ Integración directa con FastAnswersModelMC
 *
 * **Flujo de Operación:**
 * 1. Recibe el cuerpo del mensaje rápido (body)
 * 2. Llama a FastAnswersModelMC.sendMessage para enviar la respuesta
 * 3. Si ocurre error, despacha acción de error con mensaje
 *
 * @typedef {Object} FastAnswerBody
 * @property {string|number} customer_id - ID del cliente destinatario
 * @property {string} text - Texto de la respuesta rápida
 * @property {string} [platform] - Plataforma destino (opcional)
 * @property {Object} [metadata] - Metadatos adicionales (opcional)
 *
 * @param {FastAnswerBody} body - Cuerpo de la respuesta rápida a enviar
 * @returns {Promise<void>} No retorna valor, solo despacha acciones
 * @throws {Error} Si ocurre un error en el envío de la respuesta rápida
 *
 * @example
 * // Ejemplo de uso básico
 * await dispatchFastAnswersAction({ customer_id: 1, text: '¡Gracias!' })(dispatch);
 *
 * @see {@link FastAnswersModelMC.sendMessage} Para integración de modelo
 * @see {@link dispatchMessageAction} Acción relacionada para mensajes generales
 */
export const dispatchFastAnswersAction = (body) => async (dispatch) => {
  try {
    await FastAnswersModelMC.sendMessage(body);
  } catch (error) {
    dispatch({
      type: 'LOAD_DATA_FAILURE',
      error: error.message || "An unexpected error occurred."
    });
  }
};
